const PiwikTracker = require('./trackers/piwikTracker.js');
const WebTrendsTracker = require('./trackers/webTrendsTracker.js');
const XitiTracker = require('./trackers/xitiTracker.js');
const GoogleTagTracker = require('./trackers/googleTagTracker.js');
const PianoTracker = require('./trackers/pianoTracker.js');
import {
	ParsedStatisticsXml, StatsConfig, AnalyticsConfigNode, PiwikConfigNode, WebTrendsConfigNode, XitiConfigNode, PianoConfigNode,
	Tracker, TrackingType, TrackingData, TrackingPage, TrackingEvent, TrackingProperty, ResourceCredentials, GoogleTagConfigNode
} from './typings';
import UserAgentUtils from '../userAgent';

import { Options } from 'xml2js';
import LocalStorageManager from '../localStorage/manager';
const xml2js = require('xml2js').parseString;

function xml2jsPromise<T>(xmlString: string, parseOptions: {}): Promise <T> {
	return new Promise(function (resolve: (result: T) => void, reject: (err: {}) => void) {
		xml2js(xmlString, parseOptions, function (err: {}, result: T) {
			if (err) {
				reject(err);
			} else {
				resolve(result);
			}
		});
	});
}

class StatsUtils {

	static self: StatsUtils | undefined = undefined;
	config: StatsConfig;
	trackerList: Tracker[];
	queue: TrackingData[];
	resourceCredentials: ResourceCredentials | undefined;
	isMobileApp: boolean;

	parseOptions: Options;

	static getInstance() {
		if (StatsUtils.self === undefined) {
			StatsUtils.self = new StatsUtils();
		}

		return StatsUtils.self;
	}

	constructor() {
		this.parseOptions = {
			async: true,
			explicitChildren: true,

			childkey: 'children',
			attrkey: 'attrs',
		};

		this.queue = [];
		this.trackerList = [];
	}

	updateConfig(configString: string, isMobileApp: boolean, resourceCredentials?: ResourceCredentials) {
		this.isMobileApp = isMobileApp;
		this.resourceCredentials = resourceCredentials;
		xml2jsPromise<ParsedStatisticsXml>(configString, this.parseOptions)
			.then(parsedStatisticsXml => {
				if (parsedStatisticsXml.Statistics && parsedStatisticsXml.Statistics.children) {
					this.config = parsedStatisticsXml.Statistics.children;

					const localStorageQueue = LocalStorageManager.getTrackingQueue();
					if (localStorageQueue !== null) {
						this.queue = localStorageQueue.concat(this.queue);
						LocalStorageManager.setTrackingQueue(this.queue);
					}
					this.createTrackers();
					this.trackQueue();
					UserAgentUtils.subscribeToConnectionIsUp(() => this.trackQueue());
				}
			}
		);
	}

	trackPage(page: string, url: string, title: string): void {
		const trackingData: TrackingPage = {
			type: TrackingType.Page,
			arguments: {
				page,
				url,
				title,
			}
		};
		this.track(trackingData);
	}

	trackEvent(category: string, action: string, label: string, value?: number): void {
		const trackingData: TrackingEvent = {
			type: TrackingType.Event,
			arguments: {
				category,
				action,
				label,
				value,
			}
		};
		this.track(trackingData);
	}

	trackProperty(name: string, value: string): void {
		const trackingData: TrackingProperty = {
			type: TrackingType.Property,
			arguments: {
				name,
				value,
			}
		};
		this.track(trackingData);
	}

	private track(trackingData: TrackingData): void {
		this.queue.push(trackingData);
		LocalStorageManager.setTrackingQueue(this.queue);
		this.trackQueue();
	}

	private createTrackers(): void {
		this.trackerList = [];
		if (this.config.servers && this.config.servers[0]) {
			const serverConfigs = this.config.servers[0].children;

			if (serverConfigs) {
				if (serverConfigs.GoogleAnalytics) {
					this.createAnalyticsTrackers(serverConfigs.GoogleAnalytics);
				}

				if (serverConfigs.GoogleTag) {
					this.createGoogleTagTrackers(serverConfigs.GoogleTag);
				}

				if (serverConfigs.Piwik) {
					this.createPiwikTrackers(serverConfigs.Piwik);
				}

				// lib doesnt work in a mobile app
				if (serverConfigs.WebTrends && !this.isMobileApp) {
					this.createWebTrendsTrackers(serverConfigs.WebTrends);
				}

				// lib doesnt work in a mobile app
				if (serverConfigs.Xiti && !this.isMobileApp) {
					this.createXitiTrackers(serverConfigs.Xiti);
				}

				if (serverConfigs.Piano) {
					this.createPianoTrackers(serverConfigs.Piano);
				}
			}
		}
	}

	private createAnalyticsTrackers(analyticsConfigs: AnalyticsConfigNode[]) {
		for (let i = 0; i < analyticsConfigs.length; i++) {
			const analyticsConfig = analyticsConfigs[i];
			console.error('Google Analytics 3 is deprecated. Please use Google Analytics 4 instead.', analyticsConfig);
			// this.trackerList.push(new AnalyticsTracker(analyticsConfig.attrs));
		}
	}

	private createGoogleTagTrackers(googleTagConfigs: GoogleTagConfigNode[]) {
		for (let i = 0; i < googleTagConfigs.length; i++) {
			const googleTagConfig = googleTagConfigs[i];
			if (i === 0) {
				this.trackerList.push(new GoogleTagTracker(googleTagConfig.attrs));
			} else {
				console.error(`Only one GA4 property can be handled at once. Property ${googleTagConfig.attrs.account} is ignored.`);
			}
		}
	}

	private createPiwikTrackers(piwikConfigs: PiwikConfigNode[]) {
		for (let i = 0; i < piwikConfigs.length; i++) {
			const piwikConfig = piwikConfigs[i];
			this.trackerList.push(new PiwikTracker(piwikConfig.attrs));
		}
	}

	private createWebTrendsTrackers(webTrendsConfigs: WebTrendsConfigNode[]) {
		for (let i = 0; i < webTrendsConfigs.length; i++) {
			const webTrendsConfig = webTrendsConfigs[i];
			this.trackerList.push(new WebTrendsTracker(webTrendsConfig.attrs));
		}
	}

	private createXitiTrackers(xitiConfigs: XitiConfigNode[]) {
		for (let i = 0; i < xitiConfigs.length; i++) {
			let xitiConfig = xitiConfigs[i];
			if (xitiConfig.attrs.url) {
				xitiConfig.attrs.url = this.addCredentialsIfNeeded(xitiConfig.attrs.url);
			}

			this.trackerList.push(new XitiTracker(xitiConfig.attrs));
		}
	}


	private createPianoTrackers(pianoConfigs: PianoConfigNode[]) {
		for (let i = 0; i < pianoConfigs.length; i++) {
			const pianoConfig = pianoConfigs[i];
			this.trackerList.push(new PianoTracker(pianoConfig.attrs));
		}
	}

	private async trackQueue(): Promise<void> {
		const isOnline = await UserAgentUtils.isOnlinePromise();
		const shouldTrack = this.config !== undefined && isOnline;
		const maxTrackingPerbatch = 10; // maximum calls per seconds processed by google analytics
		const delayBetweenBatches = 2000;

		if (shouldTrack) {
			for (let i = 0; i < maxTrackingPerbatch; i++) {
				const tracking = this.queue.pop();
				if (tracking === undefined) {
					break;
				}

				switch (tracking.type) {
					case TrackingType.Page:
						const trackingPage = tracking as TrackingPage;
						const trackingPageArgs = trackingPage.arguments;
						this.trackPageOnAllTrackers(trackingPageArgs.page, trackingPageArgs.url, trackingPageArgs.title);
						break;
					case TrackingType.Event:
						const trackingEvent = tracking as TrackingEvent;
						const trackingEventArgs = trackingEvent.arguments;
						this.trackEventOnAllTrackers(trackingEventArgs.category, trackingEventArgs.action, trackingEventArgs.label, trackingEventArgs.value);
						break;
					case TrackingType.Property:
						const trackingProperty = tracking as TrackingProperty;
						const trackingPropertyArgs = trackingProperty.arguments;
						this.trackPropertyOnAllTrackers(trackingPropertyArgs.name, trackingPropertyArgs.value);
						break;
				}
			}

			LocalStorageManager.setTrackingQueue(this.queue);

			if (this.queue.length > 0) {
				setTimeout(() => this.trackQueue(), delayBetweenBatches);
			}
		}
	}

	private trackPageOnAllTrackers(page: string, url: string, title: string) {
		for (let i = 0; i < this.trackerList.length; i++) {
			this.trackPageIfLoaded(this.trackerList[i], page, url, title, 3);
		}
	}

	private trackEventOnAllTrackers(category: string, action: string, label: string, value?: number) {
		for (let i = 0; i < this.trackerList.length; i++) {
			this.trackEventIfLoaded(this.trackerList[i], category, action, label, value, 3);
		}
	}

	private trackPropertyOnAllTrackers(name: string, value: string) {
		for (let i = 0; i < this.trackerList.length; i++) {
			this.trackPropertyIfLoaded(this.trackerList[i], name, value, 3);
		}
	}

	// tslint:disable-next-line no-any
	private trackIfLoaded(tracker: Tracker, trackFunction: Function, args: any[], triesLeft: number) {
		if (tracker.isLoaded()) {
			trackFunction(...args);
			return;
		}
		if (triesLeft > 0) {
			setTimeout(
				() => {
					this.trackIfLoaded(tracker, trackFunction, args, triesLeft - 1);
				},
				2000
			);
		}
	}

	private trackPageIfLoaded(tracker: Tracker, page: string, url: string, title: string, triesLeft: number) {
		this.trackIfLoaded(tracker, tracker.trackPage.bind(tracker), [page, url, title], triesLeft);
	}

	private trackEventIfLoaded(tracker: Tracker, category: string, action: string, label: string, value: number|undefined, triesLeft: number) {
		this.trackIfLoaded(tracker, tracker.trackEvent.bind(tracker), [category, action, label, value], triesLeft);
	}

	private trackPropertyIfLoaded(tracker: Tracker, name: string, value: string, triesLeft: number) {
		if (tracker.trackProperty) {
			this.trackIfLoaded(tracker, tracker.trackProperty.bind(tracker), [name, value], triesLeft);
		}
	}

	private addCredentialsIfNeeded(url: string) {
		if (this.resourceCredentials === undefined) {
			return url;
		}

		const { login, password } = this.resourceCredentials;
		const includesSeparator = url.includes('?');
		const startingSeparator = includesSeparator ? '&' : '?';
		const credentials = `${startingSeparator}user=${login}&pass=${password}`;

		return url + credentials;
	}
}

export default StatsUtils;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/statistics/trackerManager.ts